import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { adminSettingsApi } from '../../../services/api'
import { Save, RefreshCw, AlertTriangle, Shield, Clock } from 'lucide-react'
import { featureFlagsService } from '../../../services/featureFlags'

// Simplified AccountSettings interface - only essential fields
interface AccountSettings {
  // All backend fields with safe defaults
  allowSelfRegistration: boolean
  emailVerificationRequired: boolean
  adminApprovalRequired: boolean
  defaultUserRole: string
  maxUsersPerOrganization: number
  passwordMinLength: number
  passwordRequireUppercase: boolean
  passwordRequireLowercase: boolean
  passwordRequireNumbers: boolean
  passwordRequireSymbols: boolean
  passwordExpiryDays: number
  accountLockoutAttempts: number
  accountLockoutDuration: number
  mfaEnabled: boolean
  mfaRequired: boolean
  mfaMethods: string[]
  mfaGracePeriod: number
  allowProfileEdit: boolean
  requirePhoneVerification: boolean
  allowAvatarUpload: boolean
  maxAvatarSize: number
  profileFieldsRequired: string[]
  customRolesEnabled: boolean
  roleHierarchy: boolean
  permissionInheritance: boolean
  permissionMatrix: { [role: string]: { [resource: string]: string[] } }
  maintenanceMode: boolean
  maintenanceMessage: string
  allowedAdminIPs: string[]
  debugMode: boolean
  logLevel: 'error' | 'warn' | 'info' | 'debug'
  apiEnabled: boolean
  apiRateLimitPerMinute: number
  apiRateLimitPerDay: number
  requireApiKey: boolean
  apiKeyRotationDays: number
  auditLogEnabled: boolean
  logUserActions: boolean
  logAdminActions: boolean
  logRetentionDays: number
  sensitiveDataLogging: boolean
  
  // CAPTCHA Protection - The only field we'll show in the UI
  captchaEnabled: boolean
  captchaProvider: 'recaptcha-v3' | 'recaptcha-v2' | 'hcaptcha' | 'turnstile'
  captchaSiteKey: string
  captchaSecretKey: string
  captchaMinScore: number
  captchaFailOpen: boolean
  captchaTimeout: number
  captchaExcludedUserIds: string[]
  captchaExcludedIPs: string[]
  captchaRolloutEnabled: boolean
  captchaRolloutPercentage: number
  captchaRolloutUserIdBased: boolean
  captchaRolloutIpBased: boolean
  
  // Coming Soon Feature Flag (OCP Extension)
  comingSoonEnabled: boolean
}

const AccountSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<AccountSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [authError, setAuthError] = useState(false)
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])
  
  // Keep comingSoonEnabled state in sync with settings.comingSoonEnabled
  useEffect(() => {
    if (settings && settings.comingSoonEnabled !== undefined) {
      if (settings.comingSoonEnabled !== comingSoonEnabled) {
        setComingSoonEnabled(settings.comingSoonEnabled)
      }
    }
  }, [settings?.comingSoonEnabled])

  const loadSettings = async () => {
    try {
      setLoading(true)
      
      const response = await adminSettingsApi.getSettings('account')
      
      // Handle different response structures
      let loadedSettings: any = null
      if (response && response.data) {
        loadedSettings = response.data
      } else if (response && (response.captchaEnabled !== undefined || response.logLevel !== undefined)) {
        // API returned response but no data property, use response directly
        loadedSettings = response
      } else {
        return
      }
      
      // Use comingSoonEnabled directly from API response (database-backed)
      // Don't override with feature flags service - the API is the source of truth
      if (loadedSettings.comingSoonEnabled !== undefined) {
        setSettings(loadedSettings)
        setComingSoonEnabled(loadedSettings.comingSoonEnabled)
        
        // Sync feature flags service with API value (for ComingSoonGate component)
        featureFlagsService.setFlag('comingSoonEnabled', loadedSettings.comingSoonEnabled)
      } else {
        // Fallback: if API doesn't have the flag, check feature flags service
        await featureFlagsService.initialize()
        const comingSoonFlag = featureFlagsService.isComingSoonEnabled()
        setSettings({ ...loadedSettings, comingSoonEnabled: comingSoonFlag })
        setComingSoonEnabled(comingSoonFlag)
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setAuthError(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCaptchaToggle = (enabled: boolean) => {
    if (!settings) return
    setSettings({ ...settings, captchaEnabled: enabled })
    setHasChanges(true)
  }

  const handleComingSoonToggle = async (enabled: boolean) => {
    if (!settings) return
    
    // Update local state
    setComingSoonEnabled(enabled)
    setSettings({ ...settings, comingSoonEnabled: enabled })
    setHasChanges(true)
    
    // Update feature flags service immediately (for instant effect)
    featureFlagsService.setFlag('comingSoonEnabled', enabled)
    
    // Show warning if enabling
    if (enabled) {
      toast.warning('Coming Soon mode enabled. All users will be redirected to the Coming Soon page.', {
        autoClose: 5000
      })
    } else {
      toast.success('Coming Soon mode disabled. Normal app access restored.', {
        autoClose: 3000
      })
    }
    
    // Force immediate refresh of feature flags from API
    // This ensures the change is reflected immediately across all components
    setTimeout(async () => {
      await featureFlagsService.refresh()
      // Dispatch custom event to notify ComingSoonGate to refresh
      window.dispatchEvent(new CustomEvent('comingSoonFlagChanged'))
    }, 500)
  }

  const handleSave = async () => {
    if (!settings) return
    
    try {
      setSaving(true)
      
      // Save coming soon flag to feature flags service
      if (settings.comingSoonEnabled !== undefined) {
        featureFlagsService.setFlag('comingSoonEnabled', settings.comingSoonEnabled)
      }
      
      const response = await adminSettingsApi.updateSettings('account', settings)
      
      // Handle different response formats
      let savedSettings: any = null
      
      if (response && typeof response === 'object') {
        // Check if it's a wrapped API response {success: true, data: settings}
        if (response.success === true && response.data) {
          savedSettings = response.data
        }
        // Check if it's a direct settings object
        else if (response.captchaEnabled !== undefined || response.logLevel !== undefined || response.comingSoonEnabled !== undefined) {
          savedSettings = response
        }
        // Unknown response format
        else {
          toast.error('Failed to save account settings - unknown response format')
          return
        }
      } else {
        toast.error('Failed to save account settings - invalid response')
        return
      }
      
      // Update state with saved settings
      if (savedSettings) {
        // Always update settings state
        setSettings(savedSettings)
        setHasChanges(false)
        
        // Always update comingSoonEnabled state from saved settings
        // Use the value from saved settings, or fallback to current state if not present
        const newComingSoonValue = savedSettings.comingSoonEnabled !== undefined 
          ? savedSettings.comingSoonEnabled 
          : settings?.comingSoonEnabled ?? false
        
        setComingSoonEnabled(newComingSoonValue)
        
        // Sync feature flags service with saved value
        featureFlagsService.setFlag('comingSoonEnabled', newComingSoonValue)
        
        // Dispatch event to notify ComingSoonGate to refresh immediately
        window.dispatchEvent(new CustomEvent('comingSoonFlagChanged'))
        
        toast.success('Account settings saved successfully!')
      }
    } catch (error: any) {
      toast.error(`Failed to save: ${error.response?.data?.error || error.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    try {
      setSaving(true)
      const response = await adminSettingsApi.resetSettings('account')
      if (response.success) {
        setSettings(response.data)
        setHasChanges(false)
        toast.success('Account settings reset to defaults!')
      }
    } catch (error) {
      toast.error('Failed to reset account settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (authError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-900 mb-2">Authentication Error</h3>
        <p className="text-red-700">You don't have permission to access account settings.</p>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Settings Found</h3>
        <p className="text-yellow-700">Unable to load account settings.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600 flex-shrink-0" />
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Account Settings</h2>
            <p className="text-gray-600 text-sm sm:text-base">Manage CAPTCHA protection for forms and authentication</p>
          </div>
        </div>

        {/* Save/Reset Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            disabled={!hasChanges || saving}
          >
            <RefreshCw className="h-4 w-4" />
            <span className="text-sm sm:text-base">Reset</span>
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            disabled={!hasChanges || saving}
          >
            <Save className="h-4 w-4" />
            <span className="text-sm sm:text-base">{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* CAPTCHA Protection Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2 text-red-600" />
          CAPTCHA Protection
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Enable CAPTCHA verification to protect forms from automated attacks and spam
        </p>

        <div className="space-y-6">
          {/* CAPTCHA Enabled Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <label className="text-base font-medium text-gray-900 block mb-1">
                CAPTCHA Protection Enabled
              </label>
              <p className="text-sm text-gray-600">
                Enable CAPTCHA verification for login, registration, and forgot password forms
              </p>
              <p className="text-xs text-gray-500 mt-2">
                When enabled, users will need to complete CAPTCHA challenges to submit forms
              </p>
            </div>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.captchaEnabled}
                  onChange={(e) => handleCaptchaToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

                 {/* CAPTCHA Configuration */}
                 {settings.captchaEnabled && (
                   <div className="space-y-4 border-t border-gray-200 pt-4">
                     <h4 className="text-base font-medium text-gray-900">CAPTCHA Configuration</h4>

                     {/* Site Key */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         reCAPTCHA Site Key
                       </label>
                       <input
                         type="text"
                         value={settings.captchaSiteKey || ''}
                         onChange={(e) => setSettings({ ...settings, captchaSiteKey: e.target.value })}
                         placeholder="Enter your reCAPTCHA site key"
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         Get your site key from <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google reCAPTCHA Console</a>
                       </p>
                     </div>

                     {/* Provider */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         CAPTCHA Provider
                       </label>
                       <select
                         value={settings.captchaProvider || 'recaptcha-v3'}
                         onChange={(e) => setSettings({ ...settings, captchaProvider: e.target.value as any })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       >
                         <option value="recaptcha-v3">Google reCAPTCHA v3</option>
                         <option value="recaptcha-v2">Google reCAPTCHA v2</option>
                         <option value="hcaptcha">hCaptcha</option>
                       </select>
                     </div>

                     {/* Minimum Score */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         Minimum Score (0.0 - 1.0)
                       </label>
                       <input
                         type="number"
                         min="0"
                         max="1"
                         step="0.1"
                         value={settings.captchaMinScore || 0.5}
                         onChange={(e) => setSettings({ ...settings, captchaMinScore: parseFloat(e.target.value) || 0.5 })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         Lower values are more strict (0.0 = very strict, 1.0 = very lenient)
                       </p>
                     </div>
                   </div>
                 )}

                 {/* Status Info */}
                 <div className={`p-4 rounded-lg ${settings.captchaEnabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                   <div className="flex items-center space-x-2">
                     <Shield className={`h-5 w-5 ${settings.captchaEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                     <span className={`text-sm font-medium ${settings.captchaEnabled ? 'text-green-900' : 'text-gray-700'}`}>
                       {settings.captchaEnabled ? 'CAPTCHA protection is currently ACTIVE' : 'CAPTCHA protection is currently DISABLED'}
                     </span>
                   </div>
                   <p className={`text-xs mt-2 ${settings.captchaEnabled ? 'text-green-700' : 'text-gray-600'}`}>
                     {settings.captchaEnabled
                       ? settings.captchaSiteKey
                         ? 'Forms are protected against automated attacks and bots'
                         : 'CAPTCHA enabled but site key not configured - protection not active'
                       : 'Forms are not protected - enable CAPTCHA to enhance security'}
                   </p>
                 </div>

                 {/* Info Note */}
                 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                   <h4 className="text-sm font-medium text-blue-900 mb-2">About CAPTCHA Protection</h4>
                   <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                     <li>Uses Google reCAPTCHA v3 (invisible - no user interaction required)</li>
                     <li>Protects login, registration, and password reset forms</li>
                     <li>Helps prevent automated attacks, spam, and credential stuffing</li>
                     <li>Configure your site key above to activate protection</li>
                   </ul>
                 </div>
        </div>
      </div>

      {/* Coming Soon Mode Section - OCP Extension */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-orange-600" />
          Coming Soon Mode
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Enable Coming Soon page to temporarily restrict access to the application. 
          When enabled, all users (including logged-in users) will only see the Coming Soon page.
        </p>

        <div className="space-y-6">
          {/* Coming Soon Enabled Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <label className="text-base font-medium text-gray-900 block mb-1">
                Enable Coming Soon Page
              </label>
              <p className="text-sm text-gray-600">
                When enabled, all routes except /coming-soon will redirect to the Coming Soon page
              </p>
              <p className="text-xs text-gray-500 mt-2">
                This affects all users regardless of authentication status. Use with caution.
              </p>
            </div>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={comingSoonEnabled}
                  onChange={(e) => handleComingSoonToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-600"></div>
              </label>
            </div>
          </div>

          {/* Status Info */}
          <div className={`p-4 rounded-lg ${comingSoonEnabled ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center space-x-2">
              <Clock className={`h-5 w-5 ${comingSoonEnabled ? 'text-orange-600' : 'text-gray-400'}`} />
              <span className={`text-sm font-medium ${comingSoonEnabled ? 'text-orange-900' : 'text-gray-700'}`}>
                {comingSoonEnabled ? 'Coming Soon mode is currently ACTIVE' : 'Coming Soon mode is currently DISABLED'}
              </span>
            </div>
            <p className={`text-xs mt-2 ${comingSoonEnabled ? 'text-orange-700' : 'text-gray-600'}`}>
              {comingSoonEnabled
                ? 'All users are being redirected to the Coming Soon page. Normal app access is blocked.'
                : 'Normal app access is available. Users can access all routes and features.'}
            </p>
          </div>

          {/* Warning Note */}
          {comingSoonEnabled && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-900 mb-2">⚠️ Important Warning</h4>
              <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                <li>Coming Soon mode blocks access to ALL pages including admin, enterprise, and government sections</li>
                <li>Only the Coming Soon page will be accessible</li>
                <li>This affects all users regardless of their role or authentication status</li>
                <li>Remember to disable Coming Soon mode when you're ready to restore normal access</li>
              </ul>
            </div>
          )}

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">About Coming Soon Mode</h4>
            <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
              <li>Instantly toggleable - changes take effect immediately</li>
              <li>Fully reversible - disable to restore normal access</li>
              <li>OCP-compliant - does not modify existing code or routes</li>
              <li>Perfect for maintenance, launches, or announcements</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Settings Coming Soon */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Additional Account Settings</h3>
        <p className="text-gray-600 text-sm">
          User management, authentication, permissions, API access, and audit logging settings will be available in a future update.
        </p>
      </div>
    </div>
  )
}

export default AccountSettingsPanel
